import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Capacitor } from '@capacitor/core';
import { AuthProvider, useAuth } from './context/AuthContext';
import { OfflineProvider } from './context/OfflineContext';
import { DataCacheProvider } from './context/DataCacheContext';
import { CountryProvider } from './context/CountryContext';
import TenantRoutes from './components/TenantRoutes';
import ScrollToTop from './components/ScrollToTop';
import OnboardingTour from './components/OnboardingTour';
import ForcePasswordChangeModal from './components/ForcePasswordChangeModal';
import Signup from './pages/Signup';
import ResetPassword from './pages/ResetPassword';
import AppOnboarding from './pages/AppOnboarding';
import HomePage from './pages/landing/HomePage';
import FeaturesPage from './pages/landing/FeaturesPage';
import PricingPage from './pages/landing/PricingPage';
import AboutPage from './pages/landing/AboutPage';
import FindBusiness from './pages/landing/FindBusiness';
import Storefront from './pages/Storefront';
import TermsOfServicePage from './pages/landing/TermsOfServicePage';
import PrivacyPolicyPage from './pages/landing/PrivacyPolicyPage';
import BlogPage from './pages/landing/BlogPage';
import BlogPost from './pages/landing/BlogPost';
import GhanaPage from './pages/landing/GhanaPage';
import NigeriaPage from './pages/landing/NigeriaPage';
import Login from './pages/Login';
import PWAHome from './pages/PWAHome';
import InstallPWA from './components/InstallPWA';
import './App.css';

const isNative = Capacitor.isNativePlatform();

// Detect if running as installed PWA (standalone mode)
const isPWA = !isNative && (
  window.matchMedia('(display-mode: standalone)').matches
  || window.navigator.standalone === true
);

// Configure native status bar
if (isNative) {
  import('@capacitor/status-bar').then(({ StatusBar, Style }) => {
    StatusBar.setStyle({ style: Style.Light });
    StatusBar.setBackgroundColor({ color: '#F5F3EE' });
    StatusBar.setOverlaysWebView({ overlay: false });
  }).catch(() => {});
}


const AppRoutes = () => {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-primaryB-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Landing / App onboarding / PWA home */}
      <Route path="/" element={isNative ? <AppOnboarding /> : isPWA ? <PWAHome /> : <><HomePage /><InstallPWA /></>} />
      <Route path="/features" element={<><FeaturesPage /><InstallPWA /></>} />
      <Route path="/pricing" element={<><PricingPage /><InstallPWA /></>} />
      <Route path="/about" element={<><AboutPage /><InstallPWA /></>} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />

      {/* Public storefront – unified or branch picker */}
      <Route path="/store/:slug" element={<Storefront />} />
      {/* Public storefront – branch-specific view */}
      <Route path="/store/:slug/:branchSlug" element={<Storefront />} />

      {/* Public routes */}
      <Route path="/signup" element={<Signup />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Login - Simple login with auto-discovery */}
      <Route path="/login" element={<Login />} />
      {/* Alternative - Find your business first */}
      <Route path="/find-business" element={<FindBusiness />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/ghana" element={<GhanaPage />} />
      <Route path="/nigeria" element={<NigeriaPage />} />

      {/* Legacy routes - redirect to new format */}
      <Route path="/super-admin/*" element={<Navigate to="/admin/dashboard" replace />} />

      {/* Tenant routes (/:slug/*) */}
      <Route path="/:slug/*" element={<TenantRoutes />} />

      {/* Catch all - redirect to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  // Android hardware back button
  useEffect(() => {
    if (!isNative) return;
    import('@capacitor/app').then(({ App: CapApp }) => {
      CapApp.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack) {
          window.history.back();
        } else {
          CapApp.minimizeApp();
        }
      });
    }).catch(() => {});
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <CountryProvider>
          <AuthProvider>
            <OfflineProvider>
              <DataCacheProvider>
                <AppRoutes />
                <OnboardingTour />
                <ForcePasswordChangeModal />
              </DataCacheProvider>
            </OfflineProvider>
          </AuthProvider>
        </CountryProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;
